// The Studio's date strip, the parts that are arithmetic: which lane a pass
// rides in, where the day rules and labels fall, and how an orbit's days read.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 86_400_000;

/** 'Sep 5' for a UTC instant. */
export function fmtMonthDay(ts: number): string {
  const d = new Date(ts);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** UTC days ('YYYY-MM-DD') as one short run, oldest first: 'Sep 5, 17',
 *  'Aug 30, Sep 11'. How a Sentinel-1 orbit is named by when it flew. */
export function fmtDayRun(days: readonly string[]): string {
  let out = '';
  let month = '';
  for (const d of [...new Set(days)].sort()) {
    const m = d.slice(0, 7);
    const day = Number(d.slice(8, 10));
    out += m === month ? `, ${day}` : `${out ? ', ' : ''}${MONTHS[Number(d.slice(5, 7)) - 1]} ${day}`;
    month = m;
  }
  return out;
}

/** VIIRS flies on three satellites in ONE orbit plane: NOAA-20 crosses a place,
 *  NOAA-21 about 25 minutes later, Suomi NPP about 25 after that. At any
 *  look-back a day's three passes share a pixel of the strip, so each satellite
 *  rides its own LANE, in crossing order — a cluster becomes a column, and
 *  every pass its own target. Scenes that aren't satellite passes get none. */
export const LANE_ORDER: readonly string[] = ['noaa20', 'noaa21', 'snpp'];

export function laneKeys(scenes: readonly { sat?: string }[]): string[] {
  const seen = new Set<string>();
  for (const s of scenes) if (s.sat) seen.add(s.sat);
  const known = LANE_ORDER.filter((k) => seen.has(k));
  const other = [...seen].filter((k) => !LANE_ORDER.includes(k)).sort();
  return [...known, ...other];
}

export interface DayMarks {
  rules: number[];                          // UTC midnights strictly inside the span
  labels: { at: number; text: string }[];   // instants to centre a day's name on
}

/** The UTC day boundaries inside [t0, t1], and the day names that fit an axis
 *  `px` wide without touching. A day wide enough is named at the middle of
 *  the part of it the span holds; narrower days are named at every k-th
 *  boundary, k counted from the epoch so the choice doesn't shift as the span
 *  moves. */
export function dayMarks(t0: number, t1: number, px: number, minGap = 46): DayMarks {
  const rules: number[] = [];
  const labels: { at: number; text: string }[] = [];
  const span = t1 - t0;
  if (!(span > 0) || !(px > 0)) return { rules, labels };
  const first = Math.floor(t0 / DAY_MS) * DAY_MS;
  for (let m = first + DAY_MS; m < t1; m += DAY_MS) rules.push(m);
  const perPx = px / span;
  if (DAY_MS * perPx >= minGap) {
    let last = -Infinity;
    for (let m = first; m < t1; m += DAY_MS) {
      const a = Math.max(m, t0);
      const b = Math.min(m + DAY_MS, t1);
      const x = ((a + b) / 2 - t0) * perPx;
      // A sliver of a day at either end has no room for its name.
      if ((b - a) * perPx < minGap * 0.6 || x - last < minGap) continue;
      labels.push({ at: (a + b) / 2, text: fmtMonthDay(m) });
      last = x;
    }
  } else {
    const k = Math.ceil(minGap / (DAY_MS * perPx));
    for (const m of rules) {
      if (Math.round(m / DAY_MS) % k === 0) labels.push({ at: m, text: fmtMonthDay(m) });
    }
  }
  return { rules, labels };
}

/** Where labels' left edges go on an axis `width` px wide, the labels sorted
 *  by `x`, their anchor: centred on it, slid to stay on the axis (into the
 *  pod's padding by 8px at most) and 4px off each other, each still reaching
 *  `reach` px past its own anchor. `fits` is false when no slide gets them
 *  apart — two passes of one VIIRS cluster, forty minutes and five pixels
 *  apart, can't both carry a name. */
export function placeLabels(items: readonly { x: number; w: number }[], width: number,
                            reach: number): { left: number[]; fits: boolean } {
  const boxes = items.map((m) => {
    const lo = Math.max(-8, m.x + reach - m.w);
    const hi = Math.max(lo, Math.min(width + 8 - m.w, m.x - reach));
    return { ...m, lo, hi, left: Math.min(hi, Math.max(lo, m.x - m.w / 2)) };
  });
  let fits = true;
  for (let i = 1; i < boxes.length; i++) {
    const p = boxes[i - 1]!;
    const q = boxes[i]!;
    const need = p.left + p.w + 4 - q.left;
    if (need <= 0) continue;
    let right = Math.min(need / 2, q.hi - q.left);
    const left = Math.min(need - right, p.left - p.lo);
    right = Math.min(need - left, q.hi - q.left);
    p.left -= left;
    q.left += right;
    if (p.left + p.w + 4 - q.left > 0.5) fits = false;
  }
  return { left: boxes.map((b) => b.left), fits };
}
